import React, { useEffect, useMemo, useRef } from 'react';
import {
  BookingRequestResponse,
  EventEdition,
  OrganizerPortalEvent,
  PERMANENT_LABEL,
  permanentBadge,
  PROMOTION_TIER_SALES,
  activePromotion,
  displayBundesland,
  formatEuro,
} from '@radevents/shared';
import { INK, MUTED, BERRY, AMBER } from '../organizerSpace';
import { Chip, LockBadge, PremiumCta, STATUS_LABELS } from './chrome';
import { TEASER_MIN_FAVORITES } from './favoriteTeaser';
import { openPromotionRequest, promoActionLabel } from './promoActions';

interface Props {
  events: OrganizerPortalEvent[];
  /**
   * Frühere Auflagen der eigenen Events. Sie reihen sich unter „Vergangene zeigen" nach Datum
   * zwischen die vergangenen Termine, genau wie auf der Veranstalterseite — ein Event wird
   * vorgerückt und nicht neu angelegt, sonst stünde dort je Event nur ein Termin.
   */
  editions?: EventEdition[];
  loading: boolean;
  includePast: boolean;
  onIncludePastChange: (value: boolean) => void;
  premium: boolean;
  /** Offene Buchungsanfragen — sie entscheiden, ob am Event „Anfrage läuft“ steht. */
  bookings: BookingRequestResponse[];
  onCreate: () => void;
  /**
   * @param intent `next-edition`, wenn der Knopf „Neuen Termin eintragen“ war — dann geht es um
   *   das Datum, und das Formular zeigt hin.
   */
  onEdit: (entry: OrganizerPortalEvent, intent?: 'next-edition') => void;
  /** Öffnet den Buchungsdialog für genau dieses Event. */
  onPromote: (entry: OrganizerPortalEvent) => void;
  /**
   * „Veröffentlichen" an einem Entwurf. Auch das ist ein Vorschlag: bei Auto-Freigabe sofort
   * online, sonst noch einmal durch die Prüfung.
   */
  onPublish?: (entry: OrganizerPortalEvent) => void;
  /** Öffnet den Premium-Dialog, den die Seite hält. */
  onPremiumHint: () => void;
  /** Es liegt schon eine Premium-Anfrage bei uns — dann ist der Haken kein Kaufweg mehr. */
  premiumRequested?: boolean;
  /**
   * Das Event, zu dem der Reiter *Sichtbarkeit & Promotion* herübergeschickt hat.
   *
   * Dort steht je Event nur der Zustand seiner Hervorhebung; gebucht wird hier. Ohne den Sprung
   * landete der Verein oben in einer Liste gleich aussehender Karten und musste den Termin, den
   * er gerade angetippt hatte, ein zweites Mal suchen.
   */
  focusEventId?: number | null;
  /** Der Sprung ist angekommen — der Rahmen darf wieder weg. */
  onFocusHandled?: () => void;
}

/**
 * Reiter *Meine Events*: die Saison als Linie, mit Hervorhebung und Premium-Haken am Termin.
 *
 * Der größte der vier Reiter und deshalb der Grund, warum `OrganizerManage` aufgeteilt wurde.
 * Inhalt und Reihenfolge sind unverändert; die Ableitungen für die Saison-Ansicht sind mit
 * hierher gezogen, weil sie nirgends sonst gebraucht werden.
 */
const EventsTab: React.FC<Props> = ({
  events, editions = [], loading, includePast, onIncludePastChange, premium, bookings,
  onCreate, onEdit, onPromote, onPublish, onPremiumHint, premiumRequested, focusEventId, onFocusHandled,
}) => {
  /**
   * Hinspringen und kurz zeigen, welcher es ist.
   *
   * Der Rahmen verschwindet nach ein paar Sekunden von selbst: er beantwortet „wo bin ich
   * gelandet?" und ist danach nur noch eine Auszeichnung ohne Aussage. Bleibt der Termin
   * ausgeblendet (vergangene Events sind zugeklappt), passiert schlicht nichts — die Liste
   * springt dann nicht an eine Stelle, an der nichts steht.
   */
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focusEventId == null) return;
    focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => onFocusHandled?.(), 2600);
    return () => clearTimeout(timer);
  }, [focusEventId, onFocusHandled]);

  // --- Ableitungen für die Saison-Ansicht ------------------------------------------------

  const startOfToday = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const dayOf = (iso: string) => new Date(`${iso}T00:00:00`);
  const daysUntil = (iso: string) =>
    Math.round((dayOf(iso).getTime() - startOfToday.getTime()) / 86_400_000);

  /**
   * „in 24 Tagen“ ist die Auskunft, die ein Verein sucht — nicht das Datum, das er ohnehin
   * auswendig kennt. Für heute und morgen gibt es eigene Wörter, alles andere zählt Tage.
   */
  const countdownWords = (days: number): [string, string] => {
    if (days === 0) return ['heute', 'Es ist so weit'];
    if (days === 1) return ['morgen', 'Nächste Veranstaltung'];
    return [String(days), days === 1 ? 'Tag bis zur Veranstaltung' : 'Tage bis zur Veranstaltung'];
  };

  /**
   * Der Premium-Haken am Termin — anstelle des Kennzahlenfelds, das ein Premium-Verein dort sieht.
   *
   * Er sitzt am Termin und nicht in einem Werbebanner, weil hier der Verlust konkret ist: die
   * Leute, die diesen Termin gemerkt haben, hören ohne Premium nichts von uns. Deshalb trägt der
   * Kicker die **echte** Favoritenzahl (sie steht in jeder öffentlichen Event-Antwort, ist also
   * kein Geheimnis) — ohne sie wäre der Satz eine leere Behauptung.
   *
   * Die drei Werte darunter sind die *bezahlte* Auskunft. Genannt wird nur, **was** es gäbe —
   * die Namen der Kennzahlen mit Schloss. Weder echte noch erfundene Ziffern: die echten gibt
   * der Server ohne Premium nicht heraus, und ausgedachte stünden im DOM und läsen sich wie eine
   * Aussage über dieses Event.
   */
  /**
   * Gelaufen und ohne neuen Termin (23.09.2026).
   *
   * Genau hier entsteht sonst der teuerste Fehler des Bereichs: der Verein will seinen Termin
   * fuers naechste Jahr eintragen, findet nur „Event eintragen" und legt ein **zweites** Event
   * an. Das erste bleibt in der Vergangenheit liegen, mit allen Vormerkungen, allen Aufrufen und
   * der Adresse, unter der Google es kennt. Deshalb bleiben diese Events in der Liste stehen,
   * auch wenn „Vergangene zeigen" aus ist, und sagen, was zu tun ist.
   */
  const nextEditionHook = (entry: OrganizerPortalEvent) => {
    if (!entry.nextEditionDue) return null;
    return (
      <div className="vb-hook vb-hook--todo">
        <div className="vb-hook-kicker">Neuer Termin fehlt</div>
        <p className="vb-hook-text" style={{ marginTop: 4 }}>
          Dieses Event ist gelaufen. Trag den Termin fürs nächste Jahr hier ein, statt ein neues
          Event anzulegen. Vormerkungen und Aufrufe bleiben dann erhalten, und der alte Termin
          steht als frühere Auflage auf der Event-Seite.
        </p>
      </div>
    );
  };

  const premiumHook = (entry: OrganizerPortalEvent, isNext: boolean) => {
    if (premium) return null;
    // **Nur am nächsten Termin.** Das Argument ist an jedem Termin dasselbe; dreimal untereinander
    // wird aus einer Erklärung ein Mahnzettel. Der nächste Termin ist der, an dem es zählt — er
    // trägt ohnehin den Countdown, und seine Zahl ist die, die gerade wächst.
    if (!isNext) return null;
    // Läuft eine Hervorhebung, steht dort schon ein Kennzahlenfeld. Der Haken tritt **an seine
    // Stelle**, nicht daneben: zwei berandete Kästen übereinander sind keine zwei Auskünfte,
    // sondern eine unlesbare.
    if (entry.promotionStats) return null;

    // Ein Satz, nicht drei. Die Favoritenzahl ist **echt** (sie steht in jeder öffentlichen
    // Event-Antwort) — ohne sie wäre der Kicker eine leere Behauptung. Der Hinweis „trag den
    // Anmeldelink ein" stand hier einmal mit drin und ist raus: das ist eine Datenfrage, kein
    // Premium-Argument, und zusammen wurde daraus ein Absatz, den niemand liest.
    // Erst ab vier: „2 Personen haben dein Event gemerkt" ist kein Argument, sondern eines
    // gegen uns (siehe TEASER_MIN_FAVORITES).
    const favs = entry.event.favoriteCount ?? 0;
    const kicker = favs >= TEASER_MIN_FAVORITES
      ? `${favs} Personen haben dieses Event vorgemerkt`
      : 'Interessierte automatisch informieren';
    const text = 'Mit Premium informieren wir sie zum Anmeldestart und 7 Tage vor dem Event.';

    return (
      <div className="vb-hook">
        <div className="vb-hook-kicker">{kicker}</div>
        {/* Satz **und** Namen stehen zusammen in der linken Spalte, der Knopf daneben —
            nicht der Knopf in einer eigenen Zeile über den Namen. Sonst bestimmt seine Höhe
            den Abstand zwischen Satz und Namen, und bei zweizeiligem Text klafft darunter
            ein Streifen Leere, der nach einem vergessenen Element aussieht. */}
        <div className="vb-hook-row">
          <div className="vb-hook-copy">
            <p className="vb-hook-text">{text}</p>
            {/* Die **Namen** der gesperrten Werte, kein Zahlenplatz und kein Weichzeichner: der
                Platzhalter sah aus wie eine Zahl, die man gleich lesen könnte, und sagte trotzdem
                nichts. Die Zahlen selbst kommen ohnehin nie mit — `/insights` antwortet ohne
                Premium gar nicht erst.

                In einer Zeile unter dem Satz, so wie sie mit Premium auch nebeneinander stünden
                (`vb-metrics-grid`): der Platz ist derselbe, nur die Werte fehlen. Jede
                Beschriftung trägt ihr eigenes Schloss — die Überschrift „Nur mit Premium"
                darüber war eine zweite Ansage neben dem Satz, der das schon sagt. */}
            <ul className="vb-locks">
              {['Aufrufe', 'Zum Kalender hinzugefügt', 'Auf deinen Link geklickt'].map(label => (
                <li key={label}>
                  <LockBadge />
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <PremiumCta requested={premiumRequested} onClick={onPremiumHint}
            className="vb-btn vb-btn--berry">Jetzt freischalten</PremiumCta>
        </div>
      </div>
    );
  };

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.event.eventDate.localeCompare(b.event.eventDate)),
    [events],
  );
  const nextEvent = useMemo(
    // Ein jederzeit fahrbares Event hat keinen Termin (Platzhalter 1970) — es ist nie „das nächste".
    () => sortedEvents.find(e => !e.event.permanent && daysUntil(e.event.eventDate) >= 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedEvents, startOfToday],
  );

  /**
   * Die Zeitachse: eigene Events und frühere Auflagen in **einer** Liste, nach Datum.
   *
   * Die Auflagen kommen nur mit, wenn die Vergangenheit sichtbar ist — sie sind per Definition
   * vergangen. Ohne sie fehlte unter „Vergangene zeigen" ausgerechnet das, was das Vorrücken
   * aufgehoben hat: der Termin des Vorjahres.
   */
  const rows = useMemo(() => {
    const list: { key: string; date: string; entry?: OrganizerPortalEvent; edition?: EventEdition }[] =
      sortedEvents.map(entry => ({ key: `e${entry.event.id}`, date: entry.event.eventDate, entry }));
    if (includePast) {
      editions.forEach(ed => list.push({ key: `a${ed.id}`, date: ed.eventDate, edition: ed }));
    }
    return list.sort((a, b) => a.date.localeCompare(b.date));
  }, [sortedEvents, editions, includePast]);

  const routesOf = (entry: OrganizerPortalEvent) => {
    const km = entry.event.distances
      .map(d => d.distanceKm)
      .filter(k => k > 0)
      .sort((a, b) => a - b);
    return km.length ? km.map(k => Math.round(k)).join(' · ') : null;
  };

  const placeOf = (entry: OrganizerPortalEvent) =>
    [entry.event.city, entry.event.bundesland ? displayBundesland(entry.event.bundesland) : null]
      .filter(Boolean).join(' · ');

  /**
   * „September 2026", nicht „SEP 26".
   *
   * Der Trenner steht einmal je Monat und hat die ganze Zeilenbreite — die Abkuerzung sparte
   * nichts und las sich auf dem Phone, wo die Zeitachse daneben fehlt, wie ein Datenfeld.
   */
  const monthLabel = (iso: string) =>
    dayOf(iso).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  const eventChip = (entry: OrganizerPortalEvent) => {
    if (entry.pendingEditId) return <Chip text="In Prüfung" tone={AMBER} />;
    if (entry.event.status !== 'PUBLISHED') {
      return <Chip text={STATUS_LABELS[entry.event.status] ?? entry.event.status} tone={MUTED} />;
    }
    // „ANZEIGE" steht auf der öffentlichen Card — hier interessiert den Inhaber, *welche* Stufe
    // läuft und wie lange noch. Das ist die Auskunft, aus der eine Verlängerung folgt.
    const promo = activePromotion(entry.event);
    if (promo) {
      return (
        <Chip
          text={`${PROMOTION_TIER_SALES[promo.tier].name} · bis ${new Date(promo.until).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`}
          tone={BERRY}
        />
      );
    }
    return null;
  };

  /**
   * Beschriftung des Hervorheben-Knopfes — oder `null`, wenn es nichts zu buchen gibt.
   *
   * Läuft schon eine Hervorhebung, die bis zur Veranstaltung reicht, wäre eine weitere Laufzeit
   * gekauftes Nichts: die Card verschwindet danach ohnehin aus den Listen. Dann bleibt nur der
   * Wechsel auf eine stärkere Stufe — und ist die stärkste bereits gebucht, verschwindet der Knopf
   * ganz, statt in eine Sackgasse zu führen.
   */
  // Beide liegen in `promoActions` — der Reiter *Sichtbarkeit & Promotion* trifft dieselbe Entscheidung
  // in seiner Event-Liste, und zwei Kopien liefen auseinander, sobald eine gepflegt wird.
  const openBookingFor = (entry: OrganizerPortalEvent) =>
    openPromotionRequest(bookings, entry.event.id);
  const promoAction = promoActionLabel;

  let lastMonth = '';

  return (
  <section>
    <div className="vb-toolbar">
      <button className="vb-btn vb-btn--primary" onClick={onCreate}>Event eintragen</button>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: MUTED }}>
        <input type="checkbox" checked={includePast} onChange={e => onIncludePastChange(e.target.checked)} />
        Vergangene zeigen
      </label>
      {/* Kein Hinweis auf die Prüfung mehr (02.09.2026). Er stand über der Liste als Regel,
          obwohl die Auskunft am Event steht: eine Änderung, die wartet, trägt dort „In Prüfung".
          Ein Satz, der dasselbe vorab und pauschal sagt, wird nach dem ersten Mal überlesen und
          steht dann nur noch als Warnung über der eigenen Arbeit. Dass geprüft wird, sagt außerdem
          die Rückmeldung nach dem Speichern — wahrheitsgemäß je nach `autoApproveEdits`. */}
    </div>

    {loading && <p style={{ color: MUTED, fontSize: 15 }}>Wird geladen…</p>}
    {!loading && sortedEvents.length === 0 && (
      <div className="vb-empty" style={{ marginTop: 20 }}>
        <p style={{ fontSize: 15, color: INK, margin: '0 0 4px' }}>Noch nichts eingetragen.</p>
        <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
          Trag dein nächstes Event ein — es erscheint sofort im Kalender und in der
          Umkreissuche.
        </p>
      </div>
    )}

    {/* Die Saison als Linie: Monate tauchen nur beim Wechsel auf, der gefüllte Knoten ist
        die nächste Veranstaltung. Ein Verein liest sein Jahr, keine Datensätze. */}
    <ul className="vb-season">
      {rows.map((row, i) => {
        // Permanente Events stehen als eigene Gruppe oben (ihr Platzhalter 1970 sortiert
        // sie ohnehin vorn) — ohne Monat, ohne Tag, nie „vergangen".
        const month = row.entry?.event.permanent ? PERMANENT_LABEL : monthLabel(row.date);
        const showMonth = month !== lastMonth;
        lastMonth = month;
        const monthHead = showMonth ? (
          <li className="vb-month" aria-hidden>
            <span className="vb-month-label">{month}</span>
            <span className="vb-month-rule" />
          </li>
        ) : null;

        // Eine frühere Auflage: dieselbe Zeile, nur ohne Knöpfe. An ihr gibt es nichts mehr zu
        // tun, und ihre Zahlen stehen auf der Event-Seite unter „Frühere Auflagen".
        if (row.edition) {
          const ed = row.edition;
          const day = dayOf(ed.eventDate);
          return (
            <React.Fragment key={row.key}>
              {monthHead}
              <li className="vb-row" style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}>
                <div className="vb-node">
                  <span className="vb-dot vb-dot--past" />
                  <span className="vb-day">{String(day.getDate()).padStart(2, '0')}</span>
                  <span className="vb-weekday">
                    {day.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}
                  </span>
                </div>
                <div className="vb-card vb-card--edition">
                  <div className="vb-card-main">
                    <p className="vb-when">
                      <time dateTime={ed.eventDate}>
                        {day.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '').toUpperCase()}
                        {' · '}
                        {day.getDate()}. {day.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '').toUpperCase()}
                      </time>
                    </p>
                    <h3 className="vb-title">{ed.title}</h3>
                    {/* Dieselben Wörter wie auf der Veranstalterseite (OrganizerSeason). */}
                    <p className="vb-meta">
                      {[ed.city, 'frühere Auflage', ed.cancelled ? 'abgesagt' : null]
                        .filter(Boolean).join(' · ')}
                      {ed.distanceKms.length > 0 && (
                        <span className="vb-routes">
                          {' · '}{ed.distanceKms.map(k => Math.round(k)).join(' · ')} <span>km</span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </li>
            </React.Fragment>
          );
        }

        const entry = row.entry!;
        const permanent = !!entry.event.permanent;
        const days = permanent ? 0 : daysUntil(entry.event.eventDate);
        const isNext = nextEvent?.event.id === entry.event.id;
        const date = dayOf(entry.event.eventDate);
        const [big, unit] = countdownWords(days);
        const countdownText = days > 1
          ? `In ${big} ${unit.replace('Tage bis zur Veranstaltung', 'Tagen')}`
          : unit === 'Es ist so weit' ? 'Heute' : 'Morgen';
        return (
          <React.Fragment key={row.key}>
            {monthHead}
            <li
              className={`vb-row${entry.event.id === focusEventId ? ' vb-row--focus' : ''}`}
              ref={entry.event.id === focusEventId ? focusRef : undefined}
              style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}
            >
              <div className="vb-node">
                <span className={`vb-dot${isNext ? ' vb-dot--next' : days < 0 ? ' vb-dot--past' : ''}`} />
                {!permanent && (
                  <>
                    <span className="vb-day">{String(date.getDate()).padStart(2, '0')}</span>
                    <span className="vb-weekday">
                      {date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}
                    </span>
                  </>
                )}
              </div>
              <div className={`vb-card${isNext ? ' vb-card--next' : ''}`}>
                <div className="vb-card-main">
                  {/* Auf dem Phone traegt die Karte ihr Datum selbst: die Zeitachse links kostet
                      dort ein Sechstel der Breite fuer drei Zeichen, und der Titel bricht dafuer
                      um. Sie steht deshalb nur im DOM **einer** der beiden Ansichten sichtbar —
                      `.vb-when` unter 700px, `.vb-node` darueber; so liest ein Vorlesegeraet das
                      Datum genau einmal. */}
                  <p className="vb-when">
                    {permanent ? permanentBadge(entry.event)?.toUpperCase() : (
                      <time dateTime={entry.event.eventDate}>
                        {date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '').toUpperCase()}
                        {' · '}
                        {date.getDate()}. {date.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '').toUpperCase()}
                      </time>
                    )}
                    {isNext && <span className="vb-when-pill">{countdownText}</span>}
                  </p>
                  {isNext && <p className="vb-countdown">{countdownText}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <h3 className={isNext ? 'vb-title vb-title--next' : 'vb-title'}>{entry.event.title}</h3>
                    {eventChip(entry)}
                  </div>
                  {/* Ort, Startzeit und Strecken in **einer** Zeile: es ist eine Auskunft
                      („was ist das für ein Event?"), und untereinander schob sie den Kopf der
                      Karte um zwei Zeilen auseinander. Die Strecken behalten ihre Schreibmaschine
                      — so stehen sie in jeder Ausschreibung. */}
                  <p className="vb-meta">
                    {placeOf(entry) || '—'}
                    {entry.event.startTime ? ` · Start ${entry.event.startTime.slice(0, 5)}` : ''}
                    {routesOf(entry) && (
                      <span className="vb-routes"> · {routesOf(entry)} <span>km</span></span>
                    )}
                  </p>
                  {(() => {
                    const open = openBookingFor(entry);
                    if (!open) return null;
                    return (
                      <p className="vb-pending">
                        {/* Zustand, Leistung, Betrag — mehr braucht die Zeile nicht. Was
                            danach passiert, steht in der Mail und im Reiter „Eingereicht".

                            **Kein „Zurückziehen" hier** (26.08.2026): die Zeile beantwortet
                            „läuft da was?", und der Knopf machte aus einer Auskunft an jedem
                            Event eine Handlung — zwischen Titel, Ort und Strecken die einzige
                            Bedienung, und ausgerechnet die rückgängig machende. Sie steht im
                            Reiter *Eingereicht*, wo alles Abgeschickte zusammenläuft. */}
                        <strong>Warten auf Zahlung:</strong>
                        {' '}{open.priceLabel} · {formatEuro(open.grossCents)}
                      </p>
                    );
                  })()}
                </div>
                {/* Kennzahlenfeld und Premium-Haken liegen als eigenes Band **zwischen** Kopf
                    und Knopfzeile, ueber die volle Kartenbreite und randlos bis an ihre Kante.
                    Drei Zonen von oben nach unten: was ist das fuer ein Event, was bringt es
                    (bzw. was fehlt), was kann ich tun. Vorher standen die Knoepfe rechts neben
                    dem Titel und das Band darunter — dann steht die Handlung ueber der
                    Begruendung, und ein zweiter Knopf („Jetzt freischalten") sitzt schraeg
                    unter der ersten Knopfgruppe. */}
                <div className="vb-card-wide">
                  {entry.promotionStats && (() => {
                    /* Nur bei laufender Buchung und nur fuer deren Zeitraum: die Zahlen
                       beantworten, was die Hervorhebung bringt, nicht wie das Event
                       insgesamt laeuft — das steht im Reiter „Zahlen".
                       Ausgerichtete Werte statt Fliesstext: drei Zahlen nebeneinander sind
                       eine Auskunft, derselbe Satz mit fetten Stellen darin ist ein Text,
                       den man liest. Was die Woerter bedeuten, steht einmal unter der
                       Liste — nicht in jeder Zeile. */
                    const promo = activePromotion(entry.event);
                    const stats = entry.promotionStats;
                    const days = promo
                      ? Math.max(0, Math.ceil((new Date(promo.until).getTime() - Date.now()) / 86_400_000))
                      : null;
                    return (
                      <div className="vb-metrics">
                        <div className="vb-metrics-head">
                          <strong>{promo ? `${PROMOTION_TIER_SALES[promo.tier].name} läuft` : 'Hervorhebung'}</strong>
                          <span>
                            {days !== null && `noch ${days} ${days === 1 ? 'Tag' : 'Tage'}, `}
                            seit {new Date(stats.from).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        <div className="vb-metrics-grid">
                          <div className="vb-metric"><b>{stats.views}</b><span>Aufrufe</span></div>
                          <div className="vb-metric"><b>{stats.uniqueVisitors}</b><span>Besucher</span></div>
                          <div className="vb-metric vb-metric--out"><b>{stats.clicks}</b><span>Auf deinen Link geklickt</span></div>
                        </div>
                      </div>
                    );
                  })()}
                  {nextEditionHook(entry)}
                  {premiumHook(entry, isNext)}
                </div>
                <div className="vb-actions">
                  {/* Ein Entwurf hat genau eine offene Handlung: ihn zu veröffentlichen. Der
                      Knopf steht vorn und kräftig, weil das Event bis dahin niemand findet;
                      eine Hervorhebung für ein unsichtbares Event gibt es nicht zu kaufen. */}
                  {entry.event.status === 'DRAFT' && onPublish && (
                    <button
                      className="vb-btn vb-btn--primary"
                      onClick={() => onPublish(entry)}
                      disabled={!!entry.pendingEditId}
                      title={entry.pendingEditId ? 'Zu diesem Event läuft schon ein Vorschlag.' : undefined}
                    >
                      Veröffentlichen
                    </button>
                  )}
                  {/* Die Hervorhebung ist das Angebot dieser Zeile und steht deshalb vorn
                      und kräftig; Bearbeiten ist die stille Pflichtaufgabe daneben. */}
                  {promoAction(entry) && (
                    openBookingFor(entry)
                      // Wie bei „Änderung wird geprüft": der Knopf bleibt stehen und sagt,
                      // warum er gerade nicht geht — verschwinden würde nur verwirren.
                      ? (
                        <button className="vb-btn" disabled title="Es liegt bereits eine Anfrage vor.">
                          Bestellung läuft
                        </button>
                      )
                      : (
                        <button
                          className="vb-btn vb-btn--primary"
                          onClick={() => onPromote(entry)}
                        >
                          {promoAction(entry)}
                        </button>
                      )
                  )}
                  {/* Ein Knopf, nicht zwei: bei „Neuer Termin fehlt" fuehrt dasselbe
                      Bearbeiten ans Ziel, es heisst dort nur, was es tut. */}
                  {/* Auch während der Prüfung: das Formular öffnet dann die laufende Änderung, und
                      was gespeichert wird, ersetzt sie. Gesperrt verleitete am 02.10.2026 dazu,
                      über die Event-Seite zehn Vorschläge nacheinander zu schicken. */}
                  <button
                    className={entry.nextEditionDue && !entry.pendingEditId ? 'vb-btn vb-btn--primary' : 'vb-btn'}
                    onClick={() => onEdit(entry, entry.nextEditionDue ? 'next-edition' : undefined)}
                  >
                    {entry.pendingEditId
                      ? 'Änderung bearbeiten'
                      : entry.nextEditionDue ? 'Neuen Termin eintragen' : 'Bearbeiten'}
                  </button>
                </div>
              </div>
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  </section>
  );
};

export default EventsTab;
